import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import AnalysisService, { AnalysisQueryParams } from "../api/AnalysisService";
import { Error } from "../api/ApiService";
import { RootState } from "../store";
import {
  AnalysisData,
  MonitorSummaryData,
  MonitorTableAnalysisItem,
  PestCount,
} from "../types/analysis";

const getStartOfCurrentYearString = () => {
  const now = new Date();
  const year = now.getFullYear();

  return `${year}-01-01`;
};

export const useAnalysis = (
  appointmentTypes: string[],
  fromDate: string,
  toDate: string,
  property_areas: string[],
  monitorTypes: string[]
) => {
  const { propertyId } = useParams();

  const [appointmentAnalysisData, setAppointmentAnalysisData] = useState<
    AnalysisData[]
  >([]);
  const [deficiencyAnalysisData, setDeficiencyAnalysisData] = useState<
    AnalysisData[]
  >([]);
  const [infestationAnalysisData, setInfestationAnalysisData] = useState<
    AnalysisData[]
  >([]);
  const [monitorSummaryAnalysisData, setMonitorSummaryAnalysisData] =
    useState<MonitorSummaryData>({
      infestation: 0,
      noInfestation: 0,
      unreachableOrMissing: 0,
      notChecked: 0,
      total: 0,
    });
  const [monitorTableAnalysisItems, setMonitorTableAnalysisItems] = useState<
    MonitorTableAnalysisItem[]
  >([]);

  const [countAppointment, setCountAppointment] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  const [loadingCount, setLoadingCount] = useState(0);

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const finishLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));

  const selectedPropertyId = useSelector(
    (state: RootState) => state.user.selectedPropertyId
  );
  const isViewOnly = useSelector((state: RootState) => state.property.viewOnly);

  const newQueryParams: AnalysisQueryParams = {
    "filter[propertyId]": isViewOnly ? propertyId : selectedPropertyId,
    from: fromDate ? fromDate : getStartOfCurrentYearString(),
    end: toDate,
    ...(appointmentTypes.length && { appointment_types: appointmentTypes }),
  };
  const [pestCounts, setPestCounts] = useState<PestCount[]>([]);

  const fetchCustomerVisitsData = useCallback(async () => {
    startLoading();

    try {
      const response = await AnalysisService.GetAnalysis(
        "appointment",
        newQueryParams
      );

      setAppointmentAnalysisData(response.data?.appointments || []);
    } catch (error) {
      const err = error as Error;
      setError(err);
    } finally {
      finishLoading();
    }
  }, [
    selectedPropertyId,
    fromDate,
    toDate,
    property_areas,
    appointmentTypes,
    monitorTypes,
  ]);

  const fetchDeficiencyData = useCallback(async () => {
    startLoading();
    try {
      const response = await AnalysisService.GetAnalysis("deficiency", {
        ...newQueryParams,
        ...(property_areas.length && {
          "filter[property_areas][]": property_areas,
        }),
      });

      setDeficiencyAnalysisData(response.data?.deficiencies || []);
    } catch (error) {
      const err = error as Error;
      setError(err);
    } finally {
      finishLoading();
    }
  }, [
    selectedPropertyId,
    fromDate,
    toDate,
    property_areas,
    appointmentTypes,
    monitorTypes,
  ]);

  const fetchInfestationData = useCallback(async () => {
    startLoading();
    try {
      const response = await AnalysisService.GetAnalysis("infestation", {
        "filter[propertyId]": isViewOnly ? propertyId : selectedPropertyId,
        ...(appointmentTypes.length && { appointment_types: appointmentTypes }),
        ...(property_areas.length && {
          "filter[property_areas][]": property_areas,
        }),
        from: fromDate ? fromDate : getStartOfCurrentYearString(),
        end: toDate,
        ...(monitorTypes.length && { "filter[monitor_types][]": monitorTypes }),
      });

      setInfestationAnalysisData(response.data?.infestations || []);
      setCountAppointment(response.data?.totalAppointments);
    } catch (error) {
      const err = error as Error;
      setError(err);
    } finally {
      finishLoading();
    }
  }, [
    selectedPropertyId,
    setCountAppointment,
    fromDate,
    toDate,
    property_areas,
    appointmentTypes,
    monitorTypes,
  ]);

  const fetchPestAnalysis = useCallback(async () => {
    startLoading();
    try {
      const response = await AnalysisService.GetPestAnalysis({
        "filter[propertyId]": isViewOnly ? propertyId : selectedPropertyId,
        ...(appointmentTypes.length && { appointment_types: appointmentTypes }),
        from: fromDate ? fromDate : getStartOfCurrentYearString(),
        end: toDate,
        ...(property_areas.length && {
          "filter[property_areas][]": property_areas,
        }),
        ...(monitorTypes.length && {
          "filter[monitor_types][]": monitorTypes,
        }),
      });

      const months = Object.keys(response.data.pestCountPerMonitor);

      const pestCountsByMonth: PestCount[] = months.map((month) => {
        const monthData = response.data.pestCountPerMonitor[month];

        const pestCounts = Object.entries(monthData.pestCount ?? [])
          .filter(([pest]) => !pest.startsWith("unknownPest"))
          .map(([pest, count]: any) => ({
            pest,
            count: typeof count.total === "number" ? count.total : 0,
            monitors: count.monitors,
            monitorsWithCatch: count.monitorsWithCatch,
            totalMonitors: count.totalMonitors,
          }));

        return {
          month,
          pestCounts,
          hasAppointments: monthData.hasAppointments,
          hasInfestation: monthData.hasInfestation,
        };
      });
      const filteredData: PestCount[] = pestCountsByMonth.filter((item) =>
        Array.isArray(item.pestCounts)
      );

      setPestCounts(filteredData);
    } catch (error) {
      const err = error as Error;
      setError(err);
    } finally {
      finishLoading();
    }
  }, [
    selectedPropertyId,
    setPestCounts,
    fromDate,
    toDate,
    property_areas,
    appointmentTypes,
    monitorTypes,
  ]);

  const fetchMonitorSummary = useCallback(async () => {
    startLoading();
    try {
      const response = await AnalysisService.GetMonitorSummary({
        "filter[propertyId]": isViewOnly ? propertyId : selectedPropertyId,
        ...(appointmentTypes.length && { appointment_types: appointmentTypes }),
        from: fromDate ? fromDate : getStartOfCurrentYearString(),
        end: toDate,
        ...(property_areas.length && {
          "filter[property_areas][]": property_areas,
        }),
        ...(monitorTypes.length && {
          "filter[monitor_types][]": monitorTypes,
        }),
      });

      setMonitorSummaryAnalysisData(response.data || []);
    } catch (error) {
      const err = error as Error;
      setError(err);
    } finally {
      finishLoading();
    }
  }, [
    selectedPropertyId,
    setMonitorSummaryAnalysisData,
    fromDate,
    toDate,
    property_areas,
    appointmentTypes,
    monitorTypes,
  ]);

  const fetchMonitorTable = useCallback(async () => {
    startLoading();
    try {
      const response = await AnalysisService.GetMonitorTable({
        "filter[propertyId]": isViewOnly ? propertyId : selectedPropertyId,
        ...(appointmentTypes.length && { appointment_types: appointmentTypes }),
        from: fromDate ? fromDate : getStartOfCurrentYearString(),
        end: toDate,
        ...(property_areas.length && {
          "filter[property_areas][]": property_areas,
        }),
        ...(monitorTypes.length && {
          "filter[monitor_types][]": monitorTypes,
        }),
      });

      setMonitorTableAnalysisItems(response.data || []);
    } catch (error) {
      const err = error as Error;
      setError(err);
    } finally {
      finishLoading();
    }
  }, [
    selectedPropertyId,
    setMonitorTableAnalysisItems,
    fromDate,
    toDate,
    property_areas,
    appointmentTypes,
    monitorTypes,
  ]);

  useEffect(() => {
    setAppointmentAnalysisData([]);
    setDeficiencyAnalysisData([]);
    setInfestationAnalysisData([]);
    setMonitorSummaryAnalysisData({
      infestation: 0,
      noInfestation: 0,
      unreachableOrMissing: 0,
      notChecked: 0,
      total: 0,
    });
    setMonitorTableAnalysisItems([]);
    setCountAppointment(0);
    setPestCounts([]);
    setError(null);
  }, [selectedPropertyId]);

  useEffect(() => {
    fetchCustomerVisitsData();
    fetchDeficiencyData();
    fetchInfestationData();
    fetchPestAnalysis();
    fetchMonitorSummary();
    fetchMonitorTable();
  }, [
    selectedPropertyId,
    fetchCustomerVisitsData,
    fetchDeficiencyData,
    fetchInfestationData,
    fetchPestAnalysis,
    fetchMonitorSummary,
    fetchMonitorTable,
    fromDate,
    toDate,
    property_areas,
    monitorTypes,
  ]);

  const loading = loadingCount > 0;

  return {
    appointmentAnalysisData,
    deficiencyAnalysisData,
    infestationAnalysisData,
    pestCounts,
    monitorSummaryAnalysisData,
    monitorTableAnalysisItems,
    countAppointment,
    loading,
    error,
  };
};
